import React, { useState } from "react";
import { SiteWide, WebLink } from "../customComponents";
import Section from "../layouts/Section";
import { notify, money } from '../../utility/Helper1';
import { withdrawalActions } from '../../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../../redux/selectors/index';
import { useNavigate } from 'react-router-dom';

// MUI Icons
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShieldIcon from "@mui/icons-material/Shield";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SendIcon from "@mui/icons-material/Send";


const QUICK_AMOUNTS = [5, 20, 50, 100, 150, 200, 300, 500, 1000];

function Withdrawal() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [loadingText, setLoadingText] = useState('Processing...');
    const [loading, setLoading] = useState(false);
    const [currencySelected, setCurrency] = useState(finance?.[0]);
    const [amount, setAmount] = useState('');
    const [withdrawalPin, setPin] = useState('');
    const [showPin, setShowPin] = useState(false);
    const [errors, setErrors] = useState({});

    const isNaira = String(currencySelected?.type) === '141' || String(currencySelected?.abbr).toUpperCase() === 'NGN';
    const defaultQuickAmounts = isNaira
        ? [5000, 10000, 20000, 50000, 100000, 150000, 200000, 300000, 500000]
        : [5, 20, 50, 100, 150, 200, 300, 500, 1000];

    const quickAmountsParsed = currencySelected?.withdraw_packages
        ? currencySelected.withdraw_packages.split(',')
            .map(item => parseFloat(item.trim()))
            .filter(val => !isNaN(val) && val > 0)
        : [];
    const quickAmounts = quickAmountsParsed.length > 0 ? quickAmountsParsed : defaultQuickAmounts;

    React.useEffect(() => {
        if (finance && finance.length > 0) {
            setCurrency(finance[0]);
        }
    }, [finance]);

    const charge = site_settings?.plans?.[0]?.mother?.withdrawalCharge ?? 0;
    const netAmt = amount && !isNaN(amount) && parseFloat(amount) > 0
        ? Math.max(0, parseFloat(amount) - (charge / 100) * parseFloat(amount)).toFixed(2)
        : null;

    const validate = () => {
        const e = {};
        if (!amount || isNaN(amount) || parseFloat(amount) <= 0) e.amount = 'Enter a valid amount';
        if (!withdrawalPin) e.pin = 'PIN is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const processWithdrawal = (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        const autoConfirm = Number(currencySelected?.autoConfirm);
        if (autoConfirm === 0) {
            if ((userData?.bank?.length ?? 0) < 3) {
                notify('Please bind your bank details before withdrawal.');
                return;
            }
        } else {
            console.log('---- userData', userData)
            const walletField = (currencySelected?.abbrRo || '').toLowerCase() + 'wallet';
            
            let walletValue = userData?.[walletField];
            if (!walletValue && userData?.bankdetails) {
                const foundDetail = userData.bankdetails.find(detail => 
                    detail.name === walletField || 
                    (detail.abbr || '').toLowerCase() === (currencySelected?.abbrRo || '').toLowerCase()
                );
                if (foundDetail) {
                    walletValue = foundDetail.value;
                }
            }

            if ((walletValue?.length ?? 0) < 5) {
                notify(`Please bind your ${(currencySelected?.abbrRo || 'wallet').toUpperCase()} wallet details before withdrawal.`);
                return;
            }
        }
        setLoading(true);
        setLoadingText('Requesting withdrawal...');
        dispatch(withdrawalActions.onCreateWithdrawal(
            { withdrawalPin, amount, currency: currencySelected?.type },
            `withdrawal/new/0`,
            response => {
                notify(response.msg, response.code);
                if (response.code == 1) { setAmount(''); setPin(''); setErrors({}); }
                setLoading(false);
            }
        ));
    };

    // ─── shared styles ────────────────────────────────────────────────────────
    const card = {
        background: "#ffffff",
        border: "1px solid rgba(59,130,246,0.08)",
        boxShadow: "0 4px 18px rgba(59,130,246,0.05)",
        borderRadius: 20,
        padding: 20,
    };

    const inputStyle = (hasError) => ({
        width: "100%",
        boxSizing: "border-box",
        padding: "13px 16px",
        borderRadius: 14,
        border: `1.5px solid ${hasError ? "#ef4444" : "rgba(59,130,246,0.18)"}`,
        background: hasError ? "rgba(239,68,68,0.03)" : "#f8fafc",
        color: "#0f172a",
        fontSize: 15,
        fontFamily: "'DM Sans',sans-serif",
        outline: "none",
        transition: "border-color 0.2s",
    });

    const label = {
        color: "#64748b",
        fontSize: 11,
        fontFamily: "'DM Sans',sans-serif",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 8,
        display: "block",
    };

    const errMsg = { color: "#ef4444", fontSize: 11, fontFamily: "'DM Sans',sans-serif", marginTop: 4 };

    return (
        <SiteWide id="main" title="Withdraw">
            <Section overlayText={loadingText} loading={loading} title="Withdraw">
                <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 24 }}>

                    {/* ── Hero balance card ───────────────────────────────── */}
                    <div style={{
                        borderRadius: 22, padding: "22px 20px", position: "relative", overflow: "hidden",
                        background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 55%,#3b82f6 100%)",
                        boxShadow: "0 10px 28px rgba(37,99,235,0.3)",
                    }}>
                        {/* glow blob */}
                        <div style={{
                            position: "absolute", top: -30, right: -30, width: 160, height: 160,
                            borderRadius: "50%", background: "radial-gradient(circle,rgba(147,197,253,0.22),transparent)",
                            pointerEvents: "none",
                        }} />

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                                <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                    Available Balance
                                </p>
                                <p style={{ color: "#fff", fontSize: 30, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "6px 0 0", lineHeight: 1.1 }}>
                                    {money(currencySelected?.balance ?? 0, currencySelected?.type)}
                                </p>
                                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
                                    <ArrowUpwardIcon style={{ fontSize: 12, color: "#86efac" }} />
                                    <span style={{ color: "#86efac", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                        {currencySelected?.name || "Local Wallet"}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/withdrawal-records')}
                                style={{
                                    width: 48, height: 48, borderRadius: "50%",
                                    background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.3)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", outline: "none", transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; e.currentTarget.style.transform = "scale(1.05)"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.transform = "scale(1)"; }}
                            >
                                <ChevronRightIcon style={{ color: "#fff", fontSize: 24 }} />
                            </button>
                        </div>
                    </div>

                    {/* Currency picker removed in profile switch update */}

                    {/* ── Amount input ─────────────────────────────────────── */}
                    <div style={card}>
                        <span style={label}>Amount</span>
                        <div style={{ position: "relative" }}>
                            <input
                                type="number"
                                readOnly
                                value={amount}
                                placeholder="0.00"
                                style={{ ...inputStyle(!!errors.amount), paddingRight: 60, fontSize: 22, fontWeight: 700, cursor: "default" }}
                            />
                            <span style={{
                                position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
                                color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", fontWeight: 600,
                            }}>
                                {currencySelected?.abbr || currencySelected?.symbol}
                            </span>
                        </div>
                        {errors.amount && <p style={errMsg}>{errors.amount}</p>}

                        {/* Quick-pick chips */}
                        <div style={{
                            display: "flex", gap: 8, flexWrap: "nowrap",
                            overflowX: "auto", marginTop: 12,
                            paddingBottom: 4,
                            /* hide scrollbar */
                            msOverflowStyle: "none", scrollbarWidth: "none",
                        }}>
                            {quickAmounts.map(n => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setAmount(n.toString())}
                                    style={{
                                        padding: "5px 13px", borderRadius: 20, cursor: "pointer", outline: "none",
                                        background: amount == n ? "rgba(37,99,235,0.1)" : "rgba(59,130,246,0.04)",
                                        border: amount == n ? "1.5px solid rgba(37,99,235,0.3)" : "1.5px solid rgba(59,130,246,0.1)",
                                        color: amount == n ? "#1d4ed8" : "#64748b",
                                        fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
                                        transition: "all 0.15s",
                                    }}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>

                        {/* Min withdrawal info */}
                        {currencySelected?.minimumWithdrawal && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
                                <InfoOutlinedIcon style={{ fontSize: 13, color: "#94a3b8" }} />
                                <span style={{ color: "#94a3b8", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                    Min withdrawal: {currencySelected.minimumWithdrawal} {currencySelected.abbr}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* ── PIN input ────────────────────────────────────────── */}
                    <div style={card}>
                        <span style={label}>Withdrawal PIN</span>
                        <div style={{ position: "relative" }}>
                            <input
                                type={showPin ? "text" : "password"}
                                value={withdrawalPin}
                                onChange={e => { setPin(e.target.value); setErrors(p => ({ ...p, pin: null })); }}
                                maxLength={6}
                                placeholder="Enter your PIN"
                                style={{ ...inputStyle(!!errors.pin), paddingRight: 52 }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPin(!showPin)}
                                style={{
                                    position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                                    background: "none", border: "none", cursor: "pointer", padding: 0,
                                    display: "flex", alignItems: "center",
                                }}
                            >
                                {showPin
                                    ? <VisibilityOffIcon style={{ color: "#94a3b8", fontSize: 18 }} />
                                    : <VisibilityIcon style={{ color: "#94a3b8", fontSize: 18 }} />
                                }
                            </button>
                        </div>
                        {errors.pin && <p style={errMsg}>{errors.pin}</p>}

                        {/* Shield note */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
                            <ShieldIcon style={{ fontSize: 13, color: "#22c55e" }} />
                            <span style={{ color: "#94a3b8", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                Your PIN is encrypted and never stored in plain text
                            </span>
                        </div>
                    </div>

                    {/* ── Summary card (shows when amount entered) ─────────── */}
                    {netAmt !== null && (
                        <div style={{
                            ...card,
                            background: "linear-gradient(135deg,rgba(34,197,94,0.06),rgba(34,197,94,0.02))",
                            border: "1px solid rgba(34,197,94,0.18)",
                        }}>
                            <p style={{ color: "#0f172a", fontSize: 13, fontWeight: 600, fontFamily: "'Syne',sans-serif", margin: "0 0 12px" }}>
                                Transaction Summary
                            </p>
                            {[
                                { label: "You send", value: `${parseFloat(amount).toFixed(2)} ${currencySelected?.abbr || ""}`, color: "#0f172a" },
                                { label: `Fee (${charge}%)`, value: `- ${((charge / 100) * parseFloat(amount)).toFixed(2)} ${currencySelected?.abbr || ""}`, color: "#ef4444" },
                                { label: "You receive", value: `${netAmt} ${currencySelected?.abbr || ""}`, color: "#16a34a", bold: true },
                            ].map(r => (
                                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                    <span style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>{r.label}</span>
                                    <span style={{ color: r.color, fontSize: r.bold ? 14 : 12, fontWeight: r.bold ? 700 : 500, fontFamily: "'Syne',sans-serif" }}>{r.value}</span>
                                </div>
                            ))}
                            <div style={{ height: 1, background: "rgba(34,197,94,0.15)", margin: "8px 0" }} />
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <CheckCircleOutlineIcon style={{ fontSize: 14, color: "#22c55e" }} />
                                <span style={{ color: "#16a34a", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                    Processing typically takes 1–24 hours
                                </span>
                            </div>
                        </div>
                    )}

                    {/* ── Submit ────────────────────────────────────────────── */}
                    <button
                        onClick={processWithdrawal}
                        disabled={loading}
                        style={{
                            width: "100%", padding: "16px 0", borderRadius: 16, border: "none",
                            background: loading ? "rgba(59,130,246,0.3)" : "linear-gradient(135deg,#1e3a8a,#2563eb)",
                            color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                            cursor: loading ? "not-allowed" : "pointer", outline: "none",
                            boxShadow: loading ? "none" : "0 6px 20px rgba(37,99,235,0.3)",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            transition: "all 0.2s",
                        }}
                    >
                        {loading
                            ? <><div style={{ width: 18, height: 18, border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> Processing...</>
                            : <><SendIcon style={{ fontSize: 18 }} /> Submit Withdrawal</>
                        }
                    </button>

                </div>

                <style>{`@keyframes spin { to { transform: rotate(360deg); } } .chips-scroll::-webkit-scrollbar { display: none; }`}</style>
            </Section>
        </SiteWide>
    );
}

export default Withdrawal;
